import React, { useCallback, useContext } from 'react';
import { cn } from '@/lib/utils';
import { StepHeader } from './components/StepHeader';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from 'venn-components';
import { Warning } from 'venn-ui-kit';
import { Checkbox } from '@/components/ui/checkbox';
import type { CombinePortfoliosOrganizeOptionEnum, CombinePortfoliosStructureOptionEnum } from 'venn-api';
import { CombinePortfoliosWizardContext } from './logic/CombinePortfoliosWizardContext';

const STRUCTURE_OPTIONS: { label: string; value: CombinePortfoliosStructureOptionEnum }[] = [
  { label: 'Combined', value: 'COMBINED' },
  { label: 'Separately', value: 'SEPARATE' },
  { label: 'By Household', value: 'HOUSEHOLD' },
  { label: 'By Client', value: 'CLIENT' },
];

const ORGANIZE_OPTIONS: { label: string; value: CombinePortfoliosOrganizeOptionEnum }[] = [
  { label: 'By Portfolio', value: 'PORTFOLIO' },
  { label: 'By Asset Class', value: 'ASSET_CLASS' },
];

const MAX_SEPARATE_PORTFOLIOS = 30;

const isOrganizeEnabled = (structureOption: CombinePortfoliosStructureOptionEnum | null) =>
  !!structureOption && ['COMBINED', 'HOUSEHOLD', 'CLIENT'].includes(structureOption);
const isIncludeHouseholdEnabled = (
  structureOption: CombinePortfoliosStructureOptionEnum | null,
  organizeOption: CombinePortfoliosOrganizeOptionEnum | null,
) =>
  !!structureOption &&
  ['COMBINED', 'CLIENT'].includes(structureOption) &&
  !!organizeOption &&
  organizeOption !== 'ASSET_CLASS';
const isIncludeClientEnabled = (
  structureOption: CombinePortfoliosStructureOptionEnum | null,
  organizeOption: CombinePortfoliosOrganizeOptionEnum | null,
) =>
  !!structureOption &&
  ['COMBINED', 'HOUSEHOLD'].includes(structureOption) &&
  !!organizeOption &&
  organizeOption !== 'ASSET_CLASS';

export const CombineSettingsStep = () => {
  const { combineSettings, setCombineSettings, configuration } = useContext(CombinePortfoliosWizardContext);
  const { structureOption, organizeOption, includeHousehold, includeClient, combinedPortfolioName } = combineSettings;
  const { portfolioInformation } = configuration;

  const onCombinedPortfolioNameChange = useCallback(
    (value: string) => {
      setCombineSettings((current) => ({
        ...current,
        combinedPortfolioName: value,
      }));
    },
    [setCombineSettings],
  );

  const onStructureChange = useCallback(
    (value: CombinePortfoliosStructureOptionEnum) => {
      setCombineSettings((current) => ({
        ...current,
        structureOption: value,
        includeClient: isIncludeClientEnabled(value, organizeOption) ? current.includeClient : false,
        includeHousehold: isIncludeHouseholdEnabled(value, organizeOption) ? current.includeHousehold : false,
      }));
    },
    [setCombineSettings, organizeOption],
  );

  const onOrganizeChange = useCallback(
    (value: CombinePortfoliosOrganizeOptionEnum) => {
      setCombineSettings((current) => ({
        ...current,
        organizeOption: value,
        includeClient: isIncludeClientEnabled(structureOption, value) ? current.includeClient : false,
        includeHousehold: isIncludeHouseholdEnabled(structureOption, value) ? current.includeHousehold : false,
      }));
    },
    [setCombineSettings, structureOption],
  );

  const onIncludeHouseholdChange = useCallback(
    (value: boolean) => {
      setCombineSettings((current) => ({
        ...current,
        includeHousehold: value,
      }));
    },
    [setCombineSettings],
  );

  const onIncludeClientChange = useCallback(
    (value: boolean) => {
      setCombineSettings((current) => ({
        ...current,
        includeClient: value,
      }));
    },
    [setCombineSettings],
  );

  const portfolioNameError = combinedPortfolioName.trim().length === 0;

  const organizeEnabled = isOrganizeEnabled(structureOption);
  const includeHouseholdEnabled = isIncludeHouseholdEnabled(structureOption, organizeOption);
  const includeClientEnabled = isIncludeClientEnabled(structureOption, organizeOption);

  return (
    <>
      <div className={cn('flex flex-col py-[24px] px-[40px] gap-[16px]')}>
        <StepHeader step={1}>Structure</StepHeader>
        <RadioGroup value={structureOption ?? undefined} onValueChange={onStructureChange} className={cn('gap-[16px]')}>
          {STRUCTURE_OPTIONS.map(({ label, value }) => {
            const disabled = value === 'SEPARATE' && portfolioInformation.length >= MAX_SEPARATE_PORTFOLIOS;
            return (
              <div key={value} className={cn('flex gap-[8px]')}>
                <RadioGroupItem
                  value={value}
                  id={value}
                  state={value === structureOption ? 'checked' : 'unchecked'}
                  disabled={disabled}
                />
                <div className={cn('flex flex-col gap-[4px] w-full')}>
                  <label
                    htmlFor={value}
                    className={cn('text-[14px] cursor-pointer font-normal', { 'text-venn-grey-60': disabled })}
                  >
                    {label}
                  </label>
                  {value === 'COMBINED' && value === structureOption && (
                    <Input
                      value={combinedPortfolioName}
                      onChange={onCombinedPortfolioNameChange}
                      refreshedStyling
                      autoFocus
                      error={portfolioNameError}
                    />
                  )}
                  {disabled && <Warning text={`Selection must be less than ${MAX_SEPARATE_PORTFOLIOS}.`} />}
                </div>
              </div>
            );
          })}
        </RadioGroup>
      </div>
      <div className={cn('border-0 border-b border-solid border-venn-grey')} />
      <div className={cn('flex flex-col gap-[16px] py-[24px] px-[40px]')}>
        <StepHeader step={2} disabled={!organizeEnabled}>
          Organize
        </StepHeader>
        {organizeEnabled && (
          <RadioGroup value={organizeOption ?? undefined} onValueChange={onOrganizeChange} className={cn('gap-[16px]')}>
            {ORGANIZE_OPTIONS.map(({ label, value }) => (
              <div key={value} className={cn('flex gap-[8px]')}>
                <RadioGroupItem value={value} id={value} state={value === organizeOption ? 'checked' : 'unchecked'} />
                <label htmlFor={value} className={cn('text-[14px] cursor-pointer font-normal')}>
                  {label}
                </label>
              </div>
            ))}
          </RadioGroup>
        )}
      </div>
      <div className={cn('border-0 border-b border-solid border-venn-grey')} />
      <div className={cn('flex flex-col gap-[16px] py-[24px] px-[40px]')}>
        <StepHeader step={3} disabled={!includeHouseholdEnabled && !includeClientEnabled}>
          Options
        </StepHeader>
        {includeHouseholdEnabled && (
          <div className={cn('flex gap-[8px] items-center')}>
            <Checkbox
              id="include-household"
              state={includeHousehold ? 'checked' : 'unchecked'}
              onCheckedChange={onIncludeHouseholdChange}
            />
            <label htmlFor="include-household" className={cn('text-[14px] cursor-pointer font-normal')}>
              Include Household
            </label>
          </div>
        )}
        {includeClientEnabled && (
          <div className={cn('flex gap-[8px] items-center')}>
            <Checkbox
              id="include-client"
              state={includeClient ? 'checked' : 'unchecked'}
              onCheckedChange={onIncludeClientChange}
            />
            <label htmlFor="include-client" className={cn('text-[14px] cursor-pointer font-normal')}>
              Include Client
            </label>
          </div>
        )}
      </div>
    </>
  );
};
